import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Box, SimpleGrid, Group } from '@mantine/core'
import { FiChevronUp, FiChevronDown } from 'react-icons/fi'
import agent from '../../app/api/agent'
import { format } from 'date-fns'
import LoadingComponent from '../../app/layout/LoadingComponents'
import { AppBadge, AppButton, AppCard, AppIconButton, AppModal, AppStack, AppText, AppTextarea, AuthGateCta, TagFilterChips } from '../../app/common/ui'
import PartPhotoGallerySection from './PartPhotoGallerySection'
import { useStore } from '../../app/stores/store'
import PartFormModal from '../inventory/PartFormModal'
import { TagCategory } from '../../app/models/tag'
import { Part, PartFormValues } from '../../app/models/part'

function formatCurrency (value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)
}

export default observer(function PartsBin () {
  const { partStore, userStore, voteStore } = useStore()
  const {
    loadParts,
    loadPendingParts,
    partsRegistry,
    pendingPartsByDate,
    loadingInitial,
    loadingPending,
    createPart,
    updatePart,
    deletePart,
    approvePart,
    rejectPart,
    loading
  } = partStore
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [editingPart, setEditingPart] = useState<Part | null>(null)
  const [partToDelete, setPartToDelete] = useState<Part | null>(null)
  const [partToReject, setPartToReject] = useState<Part | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])

  useEffect(() => {
    if (userStore.isLoggedIn) {
      loadParts()
      if (userStore.isAdmin) loadPendingParts()
    }
  }, [userStore.isLoggedIn, userStore.isAdmin, loadParts, loadPendingParts])

  const handleTagToggle = (tagId: string) => {
    const next = selectedTagIds.includes(tagId)
      ? selectedTagIds.filter((id) => id !== tagId)
      : [...selectedTagIds, tagId]
    setSelectedTagIds(next)
    loadParts(next.length ? { tagIds: next, tagMatch: 'any' } : undefined)
  }

  const handleCreateStandalone = async (part: PartFormValues) => {
    const id = await createPart(part)
    if (id) {
      toast.info('Submitted! Your content has been submitted and is being reviewed before publishing.', { autoClose: 6000 })
      setAddModalOpen(false)
      loadParts()
    }
  }

  const handleUpdateStandalone = async (id: string, part: PartFormValues) => {
    await updatePart(id, part)
    setEditingPart(null)
    loadParts()
  }

  const handleConfirmDelete = async () => {
    if (!partToDelete) return
    try {
      await deletePart(partToDelete.id)
      setPartToDelete(null)
    } catch {
      // toast/error handled by agent or store
    }
  }

  const handleApprove = async (part: Part) => {
    try {
      await approvePart(part.id)
      loadParts()
    } catch {
      // error handled by interceptor
    }
  }

  const handleRejectSubmit = async () => {
    if (!partToReject) return
    try {
      await rejectPart(partToReject.id, rejectReason)
      setPartToReject(null)
      setRejectReason('')
      loadParts()
    } catch {
      // error handled by interceptor
    }
  }

  const handlePartVote = async (part: Part, value: number) => {
    const isOwn = userStore.user?.username === part.authorUsername
    if (isOwn) return
    try {
      if (part.currentUserVote === value) {
        await agent.Votes.remove(agent.VoteTargetType.Part, part.id)
      } else {
        await agent.Votes.cast(agent.VoteTargetType.Part, part.id, value)
      }
      loadParts()
    } catch {
      // error handled by interceptor
    }
  }

  if (loadingInitial) {
    return <LoadingComponent content="Loading parts bin..." />
  }

  const parts = Array.from(partsRegistry.values()).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )
  const totalValue = parts.reduce((sum, p) => sum + (p.value || 0), 0)

  if (!userStore.isLoggedIn) {
    return (
      <Box>
        <Box mb="xl">
          <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 22, color: 'var(--gs-text)', marginBottom: 'var(--gs-xs)' }}>
            PARTS BIN
          </AppText>
          <AppText size="sm" sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-muted)' }}>
            Your spare and installed parts catalog
          </AppText>
        </Box>
        <AuthGateCta
          action="manage your parts catalog"
          pitch="Log every optic, trigger, barrel, and accessory you own — with value tracking, condition notes, and community voting."
          bullets={[
            'Track parts across all your builds in one place',
            'See the value of your entire parts inventory',
            'Earn AmmoScore when your parts are community-verified'
          ]}
        />
      </Box>
    )
  }

  const pageHeader = (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 'var(--gs-md)', marginBottom: 'var(--gs-lg)' }}>
      <Box>
        <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 22, color: 'var(--gs-text)', marginBottom: 'var(--gs-xs)' }}>
          PARTS BIN
        </AppText>
        <AppText size="sm" sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-muted)' }}>
          Your spare and installed parts catalog
        </AppText>
      </Box>
      <AppButton
        size="sm"
        onClick={() => setAddModalOpen(true)}
        sx={{ background: 'var(--gs-accent)', color: '#fff', fontFamily: 'DM Sans, sans-serif', '&:hover': { background: '#bf4a00' } }}
      >
        Add part
      </AppButton>
    </Box>
  )

  const showEmptyState = parts.length === 0 && !(userStore.isAdmin && pendingPartsByDate.length > 0)
  if (showEmptyState) {
    return (
      <Box>
        {pageHeader}
        <Box
          py="xl"
          px="lg"
          sx={{
            textAlign: 'center',
            background: 'var(--gs-surface)',
            borderRadius: 8,
            border: '1px solid var(--gs-border)'
          }}
        >
          <AppText size="lg" color="dimmed" mb="md">
            No parts in your bin yet. Add parts here or when adding parts to a firearm they'll appear here too.
          </AppText>
          <AppButton
            onClick={() => setAddModalOpen(true)}
            sx={{ background: 'var(--gs-accent)', color: '#fff', fontFamily: 'DM Sans, sans-serif', '&:hover': { background: '#bf4a00' } }}
          >
            Add part
          </AppButton>
        </Box>
        <PartFormModal
          opened={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onSave={() => {}}
          mode="standalone"
          onCreateStandalone={handleCreateStandalone}
          loading={loading}
        />
      </Box>
    )
  }

  return (
    <Box>
      {pageHeader}

      <Box mb="xl" sx={{ display: 'flex', gap: 'var(--gs-md)', flexWrap: 'wrap' }}>
        <Box
          px="md"
          py="sm"
          sx={{ background: 'var(--gs-surface)', border: '1px solid var(--gs-border)', borderRadius: 8, minWidth: 140 }}
        >
          <AppText size="xs" color="dimmed" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
            Total value
          </AppText>
          <AppText size="xl" weight={700} sx={{ fontVariantNumeric: 'tabular-nums', color: 'var(--gs-gold)' }}>
            {formatCurrency(totalValue)}
          </AppText>
        </Box>
        <Box
          px="md"
          py="sm"
          sx={{ background: 'var(--gs-surface)', border: '1px solid var(--gs-border)', borderRadius: 8, minWidth: 100 }}
        >
          <AppText size="xs" color="dimmed" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
            Parts
          </AppText>
          <AppText size="xl" weight={700}>
            {parts.length}
          </AppText>
        </Box>
      </Box>

      <TagFilterChips
        selectedTagIds={selectedTagIds}
        onToggleTag={handleTagToggle}
        categories={[TagCategory.Platform, TagCategory.PartCategory]}
      />

      {userStore.isAdmin && (
        <Box mb="xl" sx={{ background: 'var(--gs-surface)', borderRadius: 8, border: '1px solid var(--gs-border)', overflow: 'hidden' }}>
          <Box px="md" py="sm" sx={{ borderBottom: '1px solid var(--gs-border)', fontWeight: 600 }}>
            Pending parts queue
          </Box>
          {loadingPending
            ? (
            <Box p="md">
              <AppText size="sm" color="dimmed">Loading...</AppText>
            </Box>
              )
            : pendingPartsByDate.length === 0
              ? (
            <Box p="md">
              <AppText size="sm" color="dimmed">No parts awaiting approval.</AppText>
            </Box>
                )
              : (
            <SimpleGrid cols={1} breakpoints={[{ minWidth: 640, cols: 2 }, { minWidth: 960, cols: 3 }]} spacing="md" p="md">
              {pendingPartsByDate.map((part) => (
                <AppCard key={part.id} padding="md" radius="md" withBorder>
                  <Group position="apart" mb="xs">
                    <AppText weight={600} size="sm" lineClamp={1}>{part.name}</AppText>
                    {part.authorUsername && (
                      <AppText size="xs" color="dimmed">by {part.authorUsername}</AppText>
                    )}
                  </Group>
                  {(part.manufacturer || part.partNumber) && (
                    <AppText size="xs" color="dimmed" mb={4}>
                      {[part.manufacturer, part.partNumber].filter(Boolean).join(' · ')}
                    </AppText>
                  )}
                  <AppText size="sm" weight={600} sx={{ color: 'var(--gs-accent)' }}>{formatCurrency(part.value)}</AppText>
                  <Group spacing="xs" mt="sm">
                    <AppButton size="xs" variant="filled" color="green" onClick={() => handleApprove(part)} loading={loading}>
                      Approve
                    </AppButton>
                    <AppButton size="xs" variant="light" color="red" onClick={() => { setPartToReject(part); setRejectReason('') }}>
                      Reject
                    </AppButton>
                    <AppButton size="xs" variant="subtle" color="red" onClick={() => setPartToDelete(part)}>
                      Delete
                    </AppButton>
                  </Group>
                </AppCard>
              ))}
            </SimpleGrid>
                )}
        </Box>
      )}

      <SimpleGrid cols={1} breakpoints={[{ minWidth: 640, cols: 2 }, { minWidth: 960, cols: 3 }]} spacing="lg">
        {parts.map((part) => (
          <AppCard
            key={part.id}
            padding="lg"
            radius="md"
            sx={{
              background: 'var(--gs-surface)',
              border: '1px solid var(--gs-border)',
              transition: 'border-color 150ms',
              '&:hover': { borderColor: 'var(--gs-border-bright)' }
            }}
          >
            {(part.mainPhotoUrl ?? part.imageUrl) && (
              <Box
                component={Link}
                to={`/parts/${part.id}`}
                sx={{
                  display: 'block',
                  textDecoration: 'none',
                  color: 'inherit',
                  marginBottom: 12
                }}
              >
                <Box
                  component="img"
                  src={part.mainPhotoUrl ?? part.imageUrl ?? undefined}
                  alt=""
                  sx={{
                    width: '100%',
                    height: 120,
                    objectFit: 'cover',
                    borderRadius: 8
                  }}
                />
              </Box>
            )}
            {part.status === 1 && (
              <PartPhotoGallerySection part={part} onPhotosChanged={() => void loadParts(selectedTagIds.length ? { tagIds: selectedTagIds, tagMatch: 'any' } : undefined)} />
            )}
            <Group position="apart" mb="xs" align="flex-start" noWrap>
              <Group spacing="xs" sx={{ flexShrink: 0 }}>
                {userStore.user?.username !== part.authorUsername && (
                  <>
                    <AppIconButton
                      size="sm"
                      variant={part.currentUserVote === 1 ? 'filled' : 'subtle'}
                      color="gray"
                      onClick={() => handlePartVote(part, 1)}
                      disabled={voteStore.loading}
                    >
                      <FiChevronUp size={14} />
                    </AppIconButton>
                    <AppText size="sm" weight={500} sx={{ minWidth: 16, textAlign: 'center' }}>
                      {part.voteScore ?? 0}
                    </AppText>
                    <AppIconButton
                      size="sm"
                      variant={part.currentUserVote === -1 ? 'filled' : 'subtle'}
                      color="gray"
                      onClick={() => handlePartVote(part, -1)}
                      disabled={voteStore.loading}
                    >
                      <FiChevronDown size={14} />
                    </AppIconButton>
                  </>
                )}
                {userStore.user?.username === part.authorUsername && (part.voteScore ?? 0) !== 0 && (
                  <AppText size="sm" color="dimmed">Score: {part.voteScore ?? 0}</AppText>
                )}
              </Group>
              <Box
                component={Link}
                to={`/parts/${part.id}`}
                sx={{ flex: 1, minWidth: 0, textDecoration: 'none', color: 'inherit' }}
              >
                <Group spacing="xs" position="right" noWrap>
                  <AppText weight={600} size="md" lineClamp={1}>
                    {part.name}
                  </AppText>
                  {part.condition && (
                    <AppBadge size="sm" variant="light" radius="xl">
                      {part.condition}
                    </AppBadge>
                  )}
                </Group>
              </Box>
            </Group>
            <Box
              component={Link}
              to={`/parts/${part.id}`}
              sx={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
            >
              {(part.manufacturer || part.partNumber) && (
                <AppText size="xs" color="dimmed" mb={4}>
                  {[part.manufacturer, part.partNumber].filter(Boolean).join(' · ')}
                </AppText>
              )}
              {(part.tags?.length ?? 0) > 0 && (
                <Group spacing="xs" mb="xs">
                  {part.tags!.slice(0, 5).map((t) => (
                    <AppBadge key={t.id} size="sm" variant="light" radius="sm">
                      {t.displayName || t.name}
                    </AppBadge>
                  ))}
                  {(part.tags!.length ?? 0) > 5 && (
                    <AppBadge size="sm" variant="outline" radius="sm">
                      +{part.tags!.length - 5} more
                    </AppBadge>
                  )}
                </Group>
              )}
              <AppText weight={700} size="md" sx={{ color: 'var(--gs-gold)', fontVariantNumeric: 'tabular-nums' }}>
                {formatCurrency(part.value)}
              </AppText>
              <AppText size="xs" color="dimmed" mt="xs">
                Added {format(new Date(part.createdAt), 'MMM d, yyyy')}
              </AppText>
            </Box>
            {userStore.isAdmin && (
              <Group spacing="xs" mt="md" sx={{ borderTop: '1px solid var(--gs-border)', paddingTop: 12 }}>
                <AppButton size="xs" variant="subtle" onClick={() => setEditingPart(part)}>
                  Edit
                </AppButton>
                <AppButton
                  size="xs"
                  variant="subtle"
                  color="red"
                  onClick={() => setPartToDelete(part)}
                >
                  Delete
                </AppButton>
              </Group>
            )}
          </AppCard>
        ))}
      </SimpleGrid>

      <PartFormModal
        opened={addModalOpen || !!editingPart}
        onClose={() => { setAddModalOpen(false); setEditingPart(null) }}
        onSave={() => {}}
        mode="standalone"
        onCreateStandalone={handleCreateStandalone}
        initialPartFromBin={editingPart}
        onUpdateStandalone={handleUpdateStandalone}
        loading={loading}
      />

      <AppModal
        opened={!!partToDelete}
        onClose={() => setPartToDelete(null)}
        title="Delete part"
      >
        <AppText size="sm" color="dimmed" mb="md">
          Remove &quot;{partToDelete?.name}&quot; from your parts bin? This cannot be undone.
        </AppText>
        <Group position="right">
          <AppButton variant="default" onClick={() => setPartToDelete(null)}>Cancel</AppButton>
          <AppButton color="red" loading={loading} onClick={handleConfirmDelete}>
            Delete
          </AppButton>
        </Group>
      </AppModal>

      <AppModal
        opened={!!partToReject}
        onClose={() => { setPartToReject(null); setRejectReason('') }}
        title="Reject part"
      >
        <AppStack spacing="sm">
          <AppText size="sm" color="dimmed">
            Reject &quot;{partToReject?.name}&quot;? Optionally provide a reason for the submitter.
          </AppText>
          <AppTextarea
            placeholder="Reason for rejection (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.currentTarget.value)}
            minRows={2}
          />
          <Group position="right">
            <AppButton variant="default" onClick={() => { setPartToReject(null); setRejectReason('') }}>Cancel</AppButton>
            <AppButton color="red" loading={loading} onClick={handleRejectSubmit}>
              Reject
            </AppButton>
          </Group>
        </AppStack>
      </AppModal>
    </Box>
  )
})
